/**
 * <JoinGateModal /> — non-member popup for HustlePlaza's add-to-cart.
 *
 * When an unauthenticated visitor clicks "Add to cart" on the Plaza
 * storefront, we show this modal instead of adding the item. Copy
 * comes from the ``verify_gate_*`` i18n keys so it translates with
 * the LanguagePicker — every user in every language sees the same
 * message in their own tongue.
 *
 * Two exit paths:
 *   • "Join HustleSuite"       → /register (or /register?returnTo=…)
 *   • "I already have an account" → /login
 *   • "Not now"                → dismiss (item is NOT added — safe by default)
 *
 * The parent is expected to persist the pending item in
 * sessionStorage ("hp_pending_cart_item") so we can re-add it after
 * signup — but this component is purely presentational.
 */
import { useTranslation } from "react-i18next";
import { ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function JoinGateModal({
  open, onClose, onJoin, onSignIn,
}) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      data-testid="join-gate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="join-gate-close"
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          <X size={18}/>
        </button>

        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <ShieldCheck size={22} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="join-gate-title"
              data-testid="join-gate-title"
              className="font-serif-display text-2xl text-slate-900"
            >
              {t("verify_gate_title")}
            </h2>
            <p
              data-testid="join-gate-body"
              className="mt-3 text-sm text-slate-600 leading-relaxed"
            >
              {t("verify_gate_body")}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            onClick={onJoin}
            data-testid="join-gate-cta-join"
            className="flex-1 h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold"
          >
            {t("verify_gate_join")}
          </Button>
          <Button
            onClick={onSignIn}
            data-testid="join-gate-cta-signin"
            variant="outline"
            className="flex-1 h-11 rounded-full border-slate-300 text-slate-700"
          >
            {t("verify_gate_signin")}
          </Button>
        </div>
        <button
          onClick={onClose}
          data-testid="join-gate-cta-cancel"
          className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700 h-8"
        >
          {t("verify_gate_cancel")}
        </button>
      </div>
    </div>
  );
}
